/**
 * Session context: GET /v1/session (auth state + CSRF token), GET /v1/me (workspaces),
 * the selected workspace (remembered per browser), logout (clears the local recovery
 * journal — PRD C03) and a "session expired" flag raised by any 401.
 *
 * Home workspace (docs/adr/0016-department-workspaces.md): right after a sign-in (a session this
 * browser has not seen) the app opens the person's home workspace — for 13STORE staff their
 * department's ("แผนกของฉัน"). Switching workspaces afterwards is remembered as before; an
 * in-place re-sign-in (session expired) never moves the page to another workspace.
 *
 * Lists kept on this device (lib/listStore.ts): on a browser that has some, the store is opened
 * next to the bootstrap request and — once the server has said WHO is signed in and in which
 * workspace — that person's lists for that workspace are put into the in-memory cache before the
 * first page renders (at most LIST_WAIT_MS later). Signing out, being signed out, another person
 * signing in, or another workspace clears what belonged to the previous one.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, describeError } from '../api/client.js';
import type { User, WorkspaceSummary } from '../api/types.js';
import { clearAllJournals } from '../lib/idb.js';
import { forgetRecentPicks } from '../lib/recentPicks.js';
import { suppressReturnTo } from '../lib/returnTo.js';
import { clearSWR, hydrateSWR, setSWRPersistence } from '../lib/swr.js';
import { clearOfflineBundles } from '../offline/store.js';
import { lazyDialog } from '../components/LazyFallback.js';
import { whenIdle } from '../lib/lazy.js';

// Shown only when a session runs out mid-work: a chunk of its own (with the 13STORE button's styles).
const SessionExpiredDialog = lazyDialog(() => import('./SessionExpiredDialog.js'), 'SessionExpiredDialog');

export interface SessionValue {
  status: 'loading' | 'anonymous' | 'authenticated' | 'error';
  user: User | null;
  workspaces: WorkspaceSummary[];
  workspace: WorkspaceSummary | null;
  error: string | null;
  expired: boolean;
  /** Signed in with a temporary password: the app shows only the forced-change page until it is replaced. */
  mustChangePassword: boolean;
  /** Self-hosted: may manage employee accounts (ตั้งค่า › ผู้ดูแลระบบ). */
  instanceAdmin: boolean;
  /**
   * Company-server mode (GET /v1/session `selfHosted`): no plans or billing anywhere in the
   * UI, personal workspaces may invite and can be converted to a team.
   */
  selfHosted: boolean;
  refresh(): Promise<void>;
  /**
   * After a 401: check whether the browser is signed in again (e.g. in another tab) WITHOUT
   * leaving the current page. true → `expired` clears and `restoredAt` changes (the editor
   * resends its pending save); false → the page stays as it is, still expired.
   */
  recheck(): Promise<boolean>;
  /** Bumps each time a session is restored in place; editors retry their pending saves. */
  restoredAt: number;
  /** Open the in-place sign-in dialog (it also opens by itself on the first 401). */
  openSignIn(): void;
  setWorkspace(id: string): void;
  addWorkspace(ws: WorkspaceSummary): void;
  /** Change fields of a workspace in place (its place in the list stays), e.g. the menu's preferences. */
  updateWorkspace(id: string, patch: Partial<WorkspaceSummary>): void;
  /** The workspace the app opens after login (null = none set). */
  homeWorkspaceId: string | null;
  homeWorkspaceSource: 'sync' | 'user' | null;
  /** "ตั้งเป็นหน้าหลัก" (a workspace id) or follow the department again (null). */
  setHome(id: string | null): Promise<void>;
  logout(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);
const WS_KEY = 'xc.workspace';
/** Which sign-in the remembered choice belongs to (user id + a short digest of the session's CSRF token). */
const SIGNIN_KEY = 'xc.workspace.signin';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

const readStoredWorkspace = () => readStored(WS_KEY);

// ---------- lists kept on this device (lib/listStore.ts, a chunk of its own) ----------
type ListStore = typeof import('../lib/listStore.js');
let listStore: Promise<ListStore | null> | null = null;
const loadListStore = () => (listStore ??= import('../lib/listStore.js').catch(() => null));
/** Same value as listStore.ts LISTS_FLAG (kept here so that the check needs no download). */
const hasStoredLists = () => readStored('xc.lists') !== null;
/** How long the first render may wait for the device's lists (normally ~10 ms; a slow disk does not hold the page). */
const LIST_WAIT_MS = 300;

/** Signed out, or another person: nothing of the previous lists stays on the device. */
function forgetStoredLists(): Promise<void> {
  if (!listStore && !hasStoredLists()) return Promise.resolve();
  return loadListStore().then((m) => m?.clearLists()).catch(() => undefined);
}

/** From now on lists are kept for this person + workspace; resolves when theirs are in memory (or after LIST_WAIT_MS). */
function openStoredLists(userId: string, workspaceId: string | null | undefined): Promise<void> {
  if (!workspaceId) return Promise.resolve();
  const open = loadListStore().then((m) => m?.openLists(userId, workspaceId, { hydrateSWR, setSWRPersistence })).catch(() => undefined);
  return Promise.race([open.then(() => undefined), new Promise<void>((resolve) => setTimeout(resolve, LIST_WAIT_MS))]);
}

/**
 * Another person or session from now on: nothing of the previous one stays in memory, and answers
 * to its requests that are still on their way are dropped when they arrive (api/client.ts
 * `newSession`, lib/swr.ts), so they are never shown to — or stored for — the next person. The
 * palette's "ล่าสุด" on this device (lib/recentPicks.ts) goes with them.
 */
function newIdentity(): void {
  api.newSession();
  clearSWR();
  forgetRecentPicks();
}

/** Not a secret: only tells one sign-in from the next on this browser (FNV-1a of the CSRF token). */
function signInMark(userId: string, csrf: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < csrf.length; i++) h = Math.imul(h ^ csrf.charCodeAt(i), 0x01000193) >>> 0;
  return `${userId}:${h.toString(36)}`;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionValue['status']>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | null>(readStoredWorkspace);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [instanceAdmin, setInstanceAdmin] = useState(false);
  const [selfHosted, setSelfHosted] = useState(false);
  const [restoredAt, setRestoredAt] = useState(0);
  const [signInOpen, setSignInOpen] = useState(false);
  const [homeWorkspaceId, setHomeWorkspaceId] = useState<string | null>(null);
  const [homeWorkspaceSource, setHomeWorkspaceSource] = useState<'sync' | 'user' | null>(null);
  const expiredRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      // A browser with stored lists: fetch their code while the bootstrap answer is on its way (and
      // let the first render wait briefly for them). Without any, nothing waits for this module.
      const stored = hasStoredLists();
      if (stored) void loadListStore();
      // One round trip for the session and the workspaces (and usually already under way: index.html
      // starts it before this bundle has loaded).
      const boot = await api.bootstrap();
      const s = boot.session;
      setSelfHosted(!!s.selfHosted);
      if (!s.authenticated || !boot.me) {
        // Nothing of the previous person's lists stays in memory (lib/swr.ts) or on the device.
        newIdentity();
        userIdRef.current = null;
        void forgetStoredLists();
        setUser(null);
        setWorkspaces([]);
        setMustChangePassword(false);
        setInstanceAdmin(false);
        setStatus('anonymous');
        return;
      }
      const me = boot.me;
      // A sign-in this browser has not seen yet: open the home workspace ("แผนกของฉัน").
      const mark = signInMark(me.user.id, s.csrfToken ?? '');
      let wsId = readStoredWorkspace();
      if (readStored(SIGNIN_KEY) !== mark) {
        writeStored(SIGNIN_KEY, mark);
        newIdentity(); // another sign-in: no list of the session before it is shown, even for a moment
        if (me.homeWorkspaceId) {
          wsId = me.homeWorkspaceId;
          setWorkspaceId(me.homeWorkspaceId);
          writeStored(WS_KEY, me.homeWorkspaceId);
        }
      } else if (userIdRef.current && userIdRef.current !== me.user.id) newIdentity();
      userIdRef.current = me.user.id;
      // This person's lists for the workspace about to open (the same choice as `workspace` below),
      // in memory before the first page renders. Rows of anyone / anywhere else are deleted.
      const opening = openStoredLists(me.user.id, (me.workspaces.find((w) => w.id === wsId) ?? me.workspaces[0])?.id);
      if (stored) await opening;
      setUser(me.user);
      setWorkspaces(me.workspaces);
      setHomeWorkspaceId(me.homeWorkspaceId ?? null);
      setHomeWorkspaceSource(me.homeWorkspaceSource ?? null);
      setMustChangePassword(!!s.mustChangePassword);
      setInstanceAdmin(!!me.instanceAdmin);
      expiredRef.current = false;
      setExpired(false);
      setError(null);
      setStatus('authenticated');
    } catch (e) {
      setError(describeError(e));
      setStatus('error');
    }
  }, []);

  const recheck = useCallback(async () => {
    try {
      const s = await api.session();
      if (!s.authenticated) return false;
      const me = await api.me();
      if (userIdRef.current && userIdRef.current !== me.user.id) {
        // Another person signed in over the previous person's page: what is on screen (and in
        // memory, and on the device) is theirs. Start clean — the app loads again as the new person,
        // at home: no sign-in mark and no remembered workspace, so refresh() treats it as a fresh
        // sign-in, and not the page left behind (returnTo).
        newIdentity();
        userIdRef.current = null;
        writeStored(SIGNIN_KEY, null);
        writeStored(WS_KEY, null);
        suppressReturnTo();
        await forgetStoredLists();
        window.history.replaceState(null, '', window.location.pathname);
        window.location.reload();
        return true;
      }
      // Signed in again in place: remember this sign-in, but stay in the workspace on screen.
      writeStored(SIGNIN_KEY, signInMark(me.user.id, s.csrfToken ?? ''));
      setUser(me.user);
      setWorkspaces(me.workspaces);
      setHomeWorkspaceId(me.homeWorkspaceId ?? null);
      setHomeWorkspaceSource(me.homeWorkspaceSource ?? null);
      setMustChangePassword(!!s.mustChangePassword);
      setInstanceAdmin(!!me.instanceAdmin);
      expiredRef.current = false;
      setExpired(false);
      setSignInOpen(false);
      setError(null);
      setStatus('authenticated');
      setRestoredAt(Date.now());
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    api.onUnauthenticated = () => {
      // The first 401 opens the sign-in dialog once; later ones (other widgets) stay quiet.
      if (expiredRef.current) return;
      expiredRef.current = true;
      setExpired(true);
      setSignInOpen(true);
    };
    void refresh();
    return () => {
      api.onUnauthenticated = null;
    };
  }, [refresh]);

  const workspace = workspaces.find((w) => w.id === workspaceId) ?? workspaces[0] ?? null;
  // The workspace in use, readable outside React (the speed collector, perf/collector.ts, stamps its beacons with it).
  const wsId = workspace?.id ?? '';
  useEffect(() => {
    document.documentElement.dataset.xcWs = wsId;
  }, [wsId]);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      user,
      workspaces,
      workspace,
      error,
      expired,
      mustChangePassword,
      instanceAdmin,
      selfHosted,
      refresh,
      recheck,
      restoredAt,
      openSignIn: () => setSignInOpen(true),
      setWorkspace: (id) => {
        setWorkspaceId(id);
        writeStored(WS_KEY, id);
        // Lists are kept for the workspace in use only: the previous one's leave the device.
        if (user) void openStoredLists(user.id, id);
      },
      addWorkspace: (ws) => setWorkspaces((xs) => [...xs.filter((x) => x.id !== ws.id), ws]),
      updateWorkspace: (id, patch) => setWorkspaces((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x))),
      homeWorkspaceId,
      homeWorkspaceSource,
      setHome: async (id) => {
        const r = await api.setHomeWorkspace(id);
        setWorkspaces(r.workspaces);
        setHomeWorkspaceId(r.homeWorkspaceId ?? null);
        setHomeWorkspaceSource(r.homeWorkspaceSource ?? null);
      },
      logout: async () => {
        // The next person to sign in on this browser starts at home, not on the page left behind.
        suppressReturnTo();
        writeStored(SIGNIN_KEY, null);
        try {
          await api.logout();
        } finally {
          newIdentity();
          userIdRef.current = null;
          await forgetStoredLists();
          await clearAllJournals();
          await clearOfflineBundles();
          await refresh();
        }
      },
    }),
    [status, user, workspaces, workspace, error, expired, mustChangePassword, instanceAdmin, selfHosted, refresh, recheck, restoredAt, homeWorkspaceId, homeWorkspaceSource],
  );

  // Warm the dialog once signed in, so a session that runs out later finds this release's chunk at hand.
  useEffect(() => (status === 'authenticated' ? whenIdle(SessionExpiredDialog.preload, 15_000) : undefined), [status]);

  return (
    <SessionContext.Provider value={value}>
      {children}
      {/* Only over a page that was signed in: the page underneath stays mounted with its edits. */}
      {expired && status === 'authenticated' && signInOpen && <SessionExpiredDialog onClose={() => setSignInOpen(false)} />}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('SessionProvider missing');
  return v;
}
